// W-0560 — Página Reapresentação de Cheques Devolvidos (single-page Accordion).
// ADR-001 (sem #TReap), ADR-004 (Accordion), ADR-005 (mensagens literais Delphi).
import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Container, Card, Row, Col, Accordion } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import dialog from '~/Utils/dialog';
import Header from '~/Components/Header/Header';
import { selectStylesSm } from '~/Styles/reactSelectStyles';
import { listarContasPorGrupoEmpresa } from '~/Services/Service';
import BuscaCheques from './BuscaCheques';
import GridChequesLista from './GridChequesLista';
import BotoesProcessamento from './BotoesProcessamento';
import { mensagens } from './mensagens';

const ReapresentacaoCheques = () => {
  // useSelector seletivo (LICOES FE-003): traz apenas a lista de GEs (empreendimentos).
  const empreendimentos = useSelector((state) => state.empreendimentos.list);

  const [geSelecionado, setGeSelecionado] = useState(null);
  const [cheques, setCheques] = useState([]);
  const [contasDeposito, setContasDeposito] = useState([]);
  const [processando, setProcessando] = useState(false);
  const [resultadoProcessamento, setResultadoProcessamento] = useState(null);
  const [activeKey, setActiveKey] = useState('busca');

  // Auto-select GE quando o usuário tem 1 só (paridade SetaGE — MAPEAMENTO_GLOBAIS).
  useEffect(() => {
    if (
      empreendimentos &&
      empreendimentos.length === 1 &&
      !geSelecionado
    ) {
      setGeSelecionado({
        codigo: empreendimentos[0].codigo,
        descricao: empreendimentos[0].descricao,
      });
    }
  }, [empreendimentos, geSelecionado]);

  // Carregar contas para o dropdown ContaDep quando GE muda.
  // Anti-race (LICOES Ciclo 9 L3): flag `ativo` evita aplicar resposta stale.
  useEffect(() => {
    if (!geSelecionado) {
      setContasDeposito([]);
      return undefined;
    }
    let ativo = true;
    listarContasPorGrupoEmpresa(geSelecionado.codigo)
      .then((res) => {
        if (!ativo) return;
        const lista = Array.isArray(res) ? res : [];
        setContasDeposito(
          lista.map((c) => ({
            codigo_conta: c.codigo,
            descricao: c.apelido || c.descricao || '',
          }))
        );
      })
      .catch(() => {
        if (!ativo) return;
        setContasDeposito([]);
      });
    return () => {
      ativo = false;
    };
  }, [geSelecionado]);

  const opcoesGE = (empreendimentos || []).map((e) => ({
    value: e.codigo,
    label: e.descricao,
  }));

  const valorGE = geSelecionado
    ? { value: geSelecionado.codigo, label: geSelecionado.descricao }
    : null;

  const handleTrocarGE = useCallback(
    async (opt) => {
      const novoGe = opt
        ? { codigo: opt.value, descricao: opt.label }
        : null;
      if (cheques.length > 0) {
        const result = await dialog.confirm({
          title: 'Trocar grupo de empresa',
          message: mensagens.CONFIRMAR_TROCAR_GE,
        });
        if (!result || !result.proceed) return;
      }
      setGeSelecionado(novoGe);
      setCheques([]);
      setResultadoProcessamento(null);
    },
    [cheques.length]
  );

  const handleAdicionarCheque = useCallback((item) => {
    // Item vem do backend (snake_case).
    setCheques((prev) => [
      ...prev,
      {
        codigo_titulo: item.codigo_titulo,
        numero_cheque: item.numero_cheque,
        cedente: item.cedente,
        data_vencimento: item.data_vencimento,
        valor: item.valor,
        portador: item.portador,
        data_baixa: item.data_baixa,
        data_primeira_dev: item.data_primeira_dev,
        data_primeira_reap: item.data_primeira_reap,
        alinea: item.alinea,
        conta_cedente: item.conta_cedente,
        // Defaults: taxa sugerida do backend; juros sugerido idem.
        conta_deposito: null,
        taxa_digitada: Number(item.taxa_sugerida || 0),
        juros_digitado: Number(item.juros_sugerido || 0),
        taxa_sugerida: Number(item.taxa_sugerida || 0),
      },
    ]);
    toast.success(`Cheque ${item.numero_cheque} adicionado ao carrinho.`);
    setActiveKey('carrinho');
  }, []);

  const handleAtualizarCheque = useCallback((codigoTitulo, patch) => {
    setCheques((prev) =>
      prev.map((c) =>
        c.codigo_titulo === codigoTitulo ? { ...c, ...patch } : c
      )
    );
  }, []);

  const handleRemoverCheque = useCallback(async (codigoTitulo) => {
    const result = await dialog.confirm({
      title: 'Remover cheque',
      message: mensagens.CONFIRMAR_REMOVER_CHEQUE,
    });
    if (!result || !result.proceed) return;
    setCheques((prev) => prev.filter((c) => c.codigo_titulo !== codigoTitulo));
  }, []);

  return (
    <Container fluid className="p-3" style={{ paddingBottom: 80 }}>
      <Header title="Reapresentação de Cheques Devolvidos" />

      <Card className="mb-3">
        <Card.Body>
          <Row>
            <Col xs={12} md={8} lg={6}>
              <label className="small mb-1 font-weight-bold">
                Grupo de empresa
              </label>
              <Select
                value={valorGE}
                options={opcoesGE}
                onChange={handleTrocarGE}
                placeholder="Selecione um grupo de empresa..."
                isClearable={false}
                styles={selectStylesSm}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                noOptionsMessage={() => 'Nenhum GE disponível'}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Accordion activeKey={activeKey}>
        <Card>
          <Accordion.Toggle
            as={Card.Header}
            eventKey="busca"
            onClick={() =>
              setActiveKey(activeKey === 'busca' ? null : 'busca')
            }
            style={{ cursor: 'pointer' }}
          >
            <strong>Buscar cheques devolvidos</strong>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="busca">
            <BuscaCheques
              geSelecionado={geSelecionado}
              cheques={cheques}
              onAdicionar={handleAdicionarCheque}
            />
          </Accordion.Collapse>
        </Card>

        <Card className="mt-2">
          <Accordion.Toggle
            as={Card.Header}
            eventKey="carrinho"
            onClick={() =>
              setActiveKey(activeKey === 'carrinho' ? null : 'carrinho')
            }
            style={{ cursor: 'pointer' }}
          >
            <strong>
              Cheques selecionados
              {cheques.length > 0 ? ` (${cheques.length})` : ''}
            </strong>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="carrinho">
            <GridChequesLista
              cheques={cheques}
              contasDeposito={contasDeposito}
              onAtualizar={handleAtualizarCheque}
              onRemover={handleRemoverCheque}
            />
          </Accordion.Collapse>
        </Card>
      </Accordion>

      {resultadoProcessamento && (
        <Card className="mt-3">
          <Card.Header>
            <strong>Resultado do último processamento</strong>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col xs={12} md={4}>
                <div className="small text-muted">Processados</div>
                <div className="h5 mb-0 text-success">
                  {resultadoProcessamento.processados.length}
                </div>
              </Col>
              <Col xs={12} md={4}>
                <div className="small text-muted">Pulados</div>
                <div className="h5 mb-0 text-warning">
                  {resultadoProcessamento.pulados.length}
                </div>
              </Col>
              <Col xs={12} md={4}>
                <div className="small text-muted">Falhados</div>
                <div className="h5 mb-0 text-danger">
                  {resultadoProcessamento.falhados.length}
                </div>
              </Col>
            </Row>
            {resultadoProcessamento.pulados.length > 0 && (
              <div className="mt-3">
                <div className="small font-weight-bold mb-1">
                  Detalhes (pulados):
                </div>
                <ul className="small mb-0">
                  {resultadoProcessamento.pulados.map((p) => (
                    <li key={`pul-${p.codigo_titulo}`}>
                      Cheque {p.codigo_titulo}: {p.motivo}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {resultadoProcessamento.falhados.length > 0 && (
              <div className="mt-3">
                <div className="small font-weight-bold mb-1">
                  Detalhes (falhados):
                </div>
                <ul className="small mb-0">
                  {resultadoProcessamento.falhados.map((f) => (
                    <li key={`fal-${f.codigo_titulo}`}>
                      Cheque {f.codigo_titulo}: {f.erro}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      <BotoesProcessamento
        geSelecionado={geSelecionado}
        cheques={cheques}
        processando={processando}
        setProcessando={setProcessando}
        setCheques={setCheques}
        setResultadoProcessamento={setResultadoProcessamento}
      />
    </Container>
  );
};

export default ReapresentacaoCheques;
